import React from 'react';
import PropTypes from 'prop-types';
import toast from 'react-hot-toast';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { Flex, Box, Text } from 'rebass/styled-components';
import { ChevronLeft } from 'lucide-react';
import { Button } from '@getro/rombo';
import { useBodyScrollLock } from 'hooks/useBodyScrollLock';
import { useFormBuilder } from './hooks/useFormBuilder';
import { FormBuilderView } from './formBuilderView';

const validationSchema = Yup.object().shape({
  title: Yup.string().ensure().trim().required('Title is required'),
  submitButtonText: Yup.string().ensure().trim().required('Button text is required'),
  successMessageTitle: Yup.string().ensure().trim().required('Success message title is required'),
  fields: Yup.array().of(
    Yup.object().shape({
      label: Yup.string().ensure().trim().required('Label is required'),
    }),
  ),
});

export const FormBuilderDrawer = ({ collectionId, sharedListId, onClose, networkName, type, networkLogo }) => {
  useBodyScrollLock();

  const {
    formState,
    isLoading,
    isSaving,
    isDirty,
    activeTab,
    error,
    lastAddedFieldId,
    setActiveTab,
    addField,
    updateField,
    deleteField,
    reorderFields,
    updateHeader,
    updateSubmitButton,
    updateSuccessMessage,
    saveForm,
    clearLastAddedFieldId,
  } = useFormBuilder({ collectionId, sharedListId });

  const handleSave = async () => {
    const result = await saveForm();
    if (result.success) {
      onClose();
      toast.success('Form updated successfully');
    } else {
      toast.error('Failed to save form. Please try again.');
    }
  };

  if (isLoading) {
    return (
      <Flex
        sx={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          width: '100%',
          bg: 'white',
          zIndex: 9999,
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Text sx={{ fontSize: '16px', color: 'text.subtle' }}>Loading...</Text>
      </Flex>
    );
  }

  if (error || !formState) {
    return (
      <Flex
        sx={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          width: '100%',
          bg: 'white',
          zIndex: 9999,
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          gap: '16px',
        }}
      >
        <Text sx={{ fontSize: '16px', color: 'text.subtle' }}>
          {error ? 'Failed to load form. Please try again.' : 'Form not found.'}
        </Text>
        <Button variant="secondary" size="small" onClick={onClose}>
          Go back
        </Button>
      </Flex>
    );
  }

  const initialValues = formState || {
    title: '',
    description: '',
    submitButtonText: '',
    successMessageTitle: '',
    successMessageDescription: '',
    fields: [],
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      enableReinitialize
      validateOnMount
      validateOnBlur
      validateOnChange={false}
    >
      {() => (
        <Flex
          sx={{
            position: 'fixed',
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            width: '100%',
            height: '100vh',
            bg: 'white',
            zIndex: 9999,
            flexDirection: 'column',
          }}
        >
          <Flex
            sx={{
              height: '64px',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: '16px',
              borderBottom: '1px solid',
              borderColor: 'border.subtle',
              bg: 'white',
              position: 'relative',
            }}
          >
            <Flex sx={{ alignItems: 'center' }}>
              <Box
                as={ChevronLeft}
                width="32px"
                height="32px"
                onClick={onClose}
                sx={{
                  cursor: 'pointer',
                  color: 'text.subtle',
                  '&:hover': { color: 'text.dark' },
                }}
              />
              <Box
                sx={{
                  width: '1px',
                  height: '64px',
                  bg: 'border.subtle',
                  mx: '16px',
                }}
              />
              <Text
                sx={{
                  fontSize: '18px',
                  fontWeight: '600',
                  color: 'text.dark',
                }}
              >
                Edit signup form
              </Text>
              {isDirty && (
                <Box
                  sx={{
                    fontSize: '12px',
                    color: 'text.subtle',
                    fontStyle: 'italic',
                    ml: '8px',
                  }}
                >
                  (Unsaved changes)
                </Box>
              )}
            </Flex>

            <Button
              variant="primary"
              size="small"
              onClick={handleSave}
              disabled={!isDirty || isSaving || formState?.fields?.length === 0}
              loading={isSaving}
              sx={{ mr: '16px' }}
            >
              Save and publish
            </Button>
          </Flex>

          <FormBuilderView
            activeTab={activeTab}
            onTabChange={setActiveTab}
            formState={formState}
            onUpdateHeader={updateHeader}
            onAddField={addField}
            onUpdateField={updateField}
            onDeleteField={deleteField}
            onReorderFields={reorderFields}
            onUpdateSubmitButton={updateSubmitButton}
            onUpdateSuccessMessage={updateSuccessMessage}
            type={type}
            networkName={networkName}
            networkLogo={networkLogo}
            lastAddedFieldId={lastAddedFieldId}
            onClearLastAddedFieldId={clearLastAddedFieldId}
          />
        </Flex>
      )}
    </Formik>
  );
};

FormBuilderDrawer.propTypes = {
  collectionId: PropTypes.number.isRequired,
  sharedListId: PropTypes.number.isRequired,
  onClose: PropTypes.func.isRequired,
  networkName: PropTypes.string,
  networkLogo: PropTypes.string,
  type: PropTypes.oneOf(['form', 'success']),
};

FormBuilderDrawer.defaultProps = {
  networkName: '[Network Name]',
  networkLogo: null,
  type: 'form',
};
